import React, { useEffect, useState } from 'react';
import { string, object } from 'prop-types';
import { Tabs } from 'antd';
import moment from 'moment';
import CallHistoryTable from './CallHistoryTable';
import { orderBy, get, map, includes, isEmpty, split, capitalize } from 'lodash';
import Truncate from '../../shared/Truncate';
import { Text, Loading, If, NotFound, FlexContainer, ConditionalRender, Row, Image, Modal, Icon, AudioPlayer } from '../../common';
import styled, { css } from 'styled-components';
import { ANDROID_GREEN, GRAY_DARK, BLACK, PLATINUM, DARK_CHARCOAL, ARGENT } from '../../../utils/colors';
import { Query } from '../../graphql';
import GET_ORDER_COMMENTS from '../../../queries/GET_ORDER_COMMENTS';
import GET_ORDER_LOGBOOK from '../../../queries/GET_ORDER_LOGBOOK';
import GET_RECORD_BY_ITEM_ID from '../../../queries/GET_RECORD_BY_ITEM_ID';
import { genUUID } from '../../../utils/misc';
import OptimalSubstitutes from './OptimalSubstitutes';
import CustomerSelection from './CustomerSelection';

const TabPane = Tabs.TabPane;

const defaultCellStyle = {
    padding: '9px',
    borderBottom: '1px solid #eaeaea',
    whiteSpace: 'nowrap'
};

const historyCallStyles = {
    tdStyle: defaultCellStyle,
    thStyle: { ...defaultCellStyle, backgroundColor: PLATINUM }
};

const BubbleContainer = styled.div`
    padding: 10px;
    ${({ sender }) => {
        if (sender === 'shopper') {
            return css`
                background-color: #EAEAEA;
                padding-left: 30px;
                border-radius: 30px 7px 7px 0px;
            `;
        }
        return css`
            background-color: #DBE9AF;
            padding-right: 30px;
            border-radius: 7px 30px 0px 7px;
        `;
    }}
`;

const HR = styled.hr`
    width: calc(50% - 75px);
    border: 0;
    border-top: 1px solid ${GRAY_DARK};
    margin: 7px 0;
`;

const senderResolver = sender => {
    const users = {
        'shopper': 'Shopper',
        'client': 'Cliente'
    };
    return users[sender];
};

const ChatBubble = ({ message, sender, contentType, imageObject, onClick, date, status, substitutes, itemOriginal, substitution, cancel }) => {
    const justify = sender === 'shopper' ? 'flex-start' : 'flex-end';
    const messageWasRead = status === 'read';

    const dateResolver = () => {
        if (typeof date === 'object') {
            return moment.unix(get(date, 'seconds', 0)).format('HH:mm');
        };
        return moment(date, 'YYYY-MM-DD HH:mm:ss').format('HH:mm');
    };

    const contentResolver = () => {
        switch (contentType) {
            case 2:
                return <Image src={imageObject} maxHeight={200} maxWidth={500} onClick={onClick} style={{ cursor: 'pointer' }} />;
            case 3:
                const optimalSubstitutesProps = {
                    substitutes,
                    itemOriginal
                };
                return <OptimalSubstitutes {...optimalSubstitutesProps} />;
            case 4:
                const customerSelectionProps = {
                    itemOriginal,
                    substitution,
                    cancel
                };
                return <CustomerSelection {...customerSelectionProps} />;
            default:
                return (
                    <>
                        { contentType === 2 ? <Image src={imageObject} maxHeight={200} maxWidth={500} onClick={onClick} style={{ cursor: 'pointer' }} /> : <Text text={message} /> }
                    </>
                );
        };
    };
    return (
        <FlexContainer mB={10} direction='column'>
            <Row justify={justify}>
                <BubbleContainer sender={sender}>
                    <Row>
                        <Text text={senderResolver(sender)} strong />
                    </Row>
                    { contentResolver() }
                    <Row justify='end' align='center'>
                        <Text size={12} color='#888888' text={dateResolver()} />
                        <Icon name={`msg-${messageWasRead ? 'delivered' : status}`} color={messageWasRead ? ANDROID_GREEN : BLACK} left={8} />
                    </Row>
                </BubbleContainer>
            </Row>
        </FlexContainer>
    );
};

const CallHistory = ({ callHistoryType, orderId, firebase }) => {
    const fromCallAndChatHistory = includes('shopper_calls', callHistoryType);
    const [calls, setCalls] = useState([]);
    const [chat, setChat] = useState([]);
    const [firebaseLoading, setFirebaseLoading] = useState(true);
    const [filterKey, setFilterKey] = useState('calls');
    const [showImage, setShowImage] = useState(null);

    const callDocResolver = ({ call }) => {
        const getDataField = field => get(call, field, 'N/A');
        return {
                clientPhone: getDataField('clientPhone'),
                event: getDataField('event'),
                os: getDataField('os'),
                shopperEmail: getDataField('shopperEmail'),
                callStart: getDataField('callStart'),
                callEnd: getDataField('callEnd')
            };
    };

    const senderResolver = sender => split(sender, '-')[1] || 'client';

    const chatDocResolver = ({ contentType, date, imageObject, textObject, identifier, status, ...elements }) => ({
        contentType,
        date,
        imageObject: get(imageObject, 'url', null),
        message: get(textObject, 'message', ''),
        sender: senderResolver(identifier),
        onClick: () => setShowImage(get(imageObject, 'url', null)),
        status,
        itemOriginal: get(elements, 'itemOriginal', null),
        substitutes: get(elements, 'substitutes', null),
        substitution: get(elements, 'substitution', null),
        cancel: get(elements, 'cancel', false)
    });

    useEffect(() => {
        if (fromCallAndChatHistory) {
            (async () => {
                const unsubscribeChat = await firebase.collection('clientShopperChat')
                    .doc(orderId)
                    .collection('messages')
                    .onSnapshot(({ docs }) => {
                        const getChat = map(docs, doc => {
                            const data = doc.data();
                            return chatDocResolver(data);
                        });
                        setChat(getChat);
                    });
                    const unsubscribeLog = await firebase.collection('order-logs')
                    .doc(orderId)
                    .collection('calls')
                    .onSnapshot(({ docs }) => {
                        const getCalls = map(docs, doc => {
                            const data = doc.data();
                            return callDocResolver(data);
                        });
                        setCalls(getCalls);
                        setFirebaseLoading(false);
                    });
                    return () => {
                        unsubscribeLog();
                        unsubscribeChat();
                    };
                })();
            }
    }, []);

    const onChangeTab = (key) => setFilterKey(key);

    const getColumns = () => {
        const columns = {
            call_history: [
                {
                    title: 'Fecha',
                    dataIndex: 'createdAt',
                    key: 'createdAt',
                    thStyle: { width: '120px', ...defaultCellStyle },
                    tdStyle: defaultCellStyle,
                    render: (value) => <Text nowrap text={value} />
                },
                {
                    title: 'Origen',
                    dataIndex: 'dataSource',
                    key: 'dataSource',
                    thStyle: { width: '45px', ...defaultCellStyle },
                    tdStyle: defaultCellStyle,
                    render: value => <Text text={value} />
                },
                {
                    title: 'Usuario',
                    dataIndex: 'user',
                    key: 'user',
                    thStyle: { width: '200px', ...defaultCellStyle },
                    tdStyle: defaultCellStyle,
                    render: value => <Truncate maxWidth={200}><Text nowrap text={value} /></Truncate>
                },
                {
                    title: 'Mensajes',
                    dataIndex: 'comment',
                    key: 'comment',
                    thStyle: defaultCellStyle,
                    tdStyle: defaultCellStyle,
                    render: value => (
                        <div>
                            <Text text={value} />
                        </div>
                    )
                }
            ],
            shopper_calls: [
                {
                    title: '',
                    dataIndex: 'url',
                    key: 'hasAudio',
                    ...historyCallStyles,
                    render: value => value && <Icon name='record' color={PLATINUM} />
                },
                {
                    title: 'Shopper',
                    dataIndex: 'pickerId',
                    key: 'pickerId',
                    ...historyCallStyles,
                    render: value => <Text text={value} size={10} />
                },
                {
                    title: 'Teléfono destino',
                    dataIndex: 'destinationNumber',
                    key: 'destinationNumber',
                    ...historyCallStyles,
                    render: value => <Text text={value} size={10} />
                },
                {
                    title: 'OS',
                    dataIndex: 'os',
                    key: 'os',
                    ...historyCallStyles,
                    render: value => <Text text={capitalize(value)} size={10} />
                },
                {
                    title: 'Fecha',
                    dataIndex: 'creationTime',
                    key: 'date',
                    ...historyCallStyles,
                    render: value => {
                        return <Text text={moment(value, 'DD-MM-YYYY').format('DD-MM-YYYY')} size={10} />;
                    }
                },
                {
                    title: 'Hora',
                    dataIndex: 'creationTime',
                    key: 'hour',
                    ...historyCallStyles,
                    render: value => <Text text={moment(value, 'YYYY-MM-DD HH:mm:ss').format('HH:mm')} size={10} />
                },
                {
                    title: 'Duración',
                    dataIndex: 'duration',
                    key: 'duration',
                    ...historyCallStyles,
                    render: value => <Text text={value} size={10} />
                },
                {
                    title: 'Contacto',
                    dataIndex: 'status',
                    key: 'status',
                    ...historyCallStyles,
                    render: value => <Text text={value} size={10} />
                },
                {
                    title: 'Acciones',
                    dataIndex: 'url',
                    key: 'actions',
                    ...historyCallStyles,
                    render: (value, data) => {
                        if (!value) return null;
                        return <AudioPlayer id={get(data, 'id', '0')} file={value} />;
                    }
                }
            ]
        };
        return get(columns, callHistoryType, get(columns, 'call_history'));
    };

    const tabs = [
        { key: 'calls', label: 'Llamadas' },
        { key: 'chat', label: 'Chat' }
    ];

    const queryProperties = fromCallAndChatHistory
        ? {
            key: 'records',
            query: GET_RECORD_BY_ITEM_ID,
            variables: { itemId: orderId }
        }
        : {
            key: 'comments',
            query: callHistoryType === 'logbook' ? GET_ORDER_LOGBOOK : GET_ORDER_COMMENTS,
            keyGetData: callHistoryType === 'logbook' ? 'getLogByOrderId' : 'getCommentsbyOrderId',
            variables: { orderId },
            skip: fromCallAndChatHistory
        };

    // This method merge shopper calls from Firebase & shopper calls from Messagebird
    const shopperCallsResolver = (recordsByOrderIdInput) => {
        // This method lets to parse calls from firebase and make it compatible with MessageBird calls at redering moment.
        const callsFromFirebase = map(calls, ({ callStart, shopperEmail, clientPhone, ...fields }) => ({
            ...fields,
            id: genUUID(),
            pickerId: shopperEmail,
            destinationNumber: clientPhone,
            creationTime: moment(callStart, 'X').format('DD-MM-YYYY HH:mm'),
            duration: (() => {
                if (callStart === 'N/A') return 'Solo hay registro del fin de llamada';
                const start = moment(callStart, 'X');
                const end = moment(get(fields, 'callEnd', moment()), 'X');
                return moment(end.diff(start, 'X')).format('mm:ss');
            })(),
            status: (() => {
                const start = moment(callStart, 'X');
                const end = moment(get(fields, 'callEnd', moment()), 'X');
                const getTimeUnit = unit => parseInt(moment(end.diff(start, 'X')).format(unit));
                return ((getTimeUnit('mm') > 0) || (getTimeUnit('ss') > 30)) ? 'Exitoso' : 'Intento de contacto';
            })(),
            url: false,
            fromFirebase: true
        }));
        const callsFromMessageBird = get(recordsByOrderIdInput, 'recordByItemId.data', []);
        const shopperCalls = orderBy([...callsFromFirebase, ...callsFromMessageBird], 'creationTime', 'desc');
        return shopperCalls;
    };

    return (
        <Query {...queryProperties} fetchPolicy='network-only'>
            {({ data: graphData, loading }) => {
                const data = get(queryProperties, 'key') === 'comments'
                    ? orderBy(get(graphData, `${get(queryProperties, 'keyGetData')}.results`, []), 'createdAt', 'desc')
                    : shopperCallsResolver(graphData);
                return (
                    <Loading loading={loading}>
                        <Modal title={null} visible={showImage} onClose={() => setShowImage(null)}>
                            <Image src={showImage} maxHeight={700} />
                        </Modal>
                        <FlexContainer direction='column' flex={1} style={{ maxHeight: 'calc(100vh - 100px)' }}>
                            <ConditionalRender condition={fromCallAndChatHistory}>
                                <FlexContainer display='block' mX={-28} mB={17}>
                                    <Tabs defaultActiveKey='calls' onChange={onChangeTab}>
                                        { map(tabs, ({ key, label }) => <TabPane tab={<Text strong text={label} color={filterKey === key ? DARK_CHARCOAL : ARGENT} />} key={key} />) }
                                    </Tabs>
                                </FlexContainer>
                            </ConditionalRender>
                            <ConditionalRender condition={filterKey === 'calls'}>
                                <If
                                    condition={isEmpty(data)}
                                    then={<NotFound firstTextLine='Este pedido no tiene llamadas registradas' />}
                                    else={
                                        <CallHistoryTable
                                            columns={getColumns()}
                                            data={data}
                                        />
                                    }
                                />
                            </ConditionalRender>
                            <ConditionalRender condition={filterKey === 'chat'}>
                                <If
                                    condition={isEmpty(chat)}
                                    then={<NotFound firstTextLine='Este pedido no tiene chat registrado' />}
                                    else={
                                        <FlexContainer
                                            border='1px solid #eaeaea'
                                            mT={50}
                                            borderRadius={5}
                                            padding={16}
                                            style={{ maxHeight: 'calc(100vh - 200px)' }}
                                        >
                                            <FlexContainer direction='column' flex={1} overflowY>
                                                { map(chat, (message, index) => {
                                                    const dayIsAfter = moment(get(message, 'date'), 'YYYY-MM-DD').isAfter(moment(get(chat[index - 1], 'date'), 'YYYY-MM-DD'));
                                                    return (
                                                        <>
                                                            <If
                                                                condition={dayIsAfter}
                                                                then={
                                                                    <FlexContainer align='center' justify='space-between' pT={10} pB={20}>
                                                                        <HR />
                                                                        <FlexContainer>
                                                                            <Text text={moment(get(message, 'date'), 'YYYY-MM-DD').format('DD [de] MMMM')} color={GRAY_DARK} strong />
                                                                        </FlexContainer>
                                                                        <HR />
                                                                    </FlexContainer>
                                                                }
                                                            />
                                                            <ChatBubble {...message} />
                                                        </>
                                                    );
                                                }) }
                                            </FlexContainer>
                                        </FlexContainer>
                                    }
                                />
                            </ConditionalRender>
                        </FlexContainer>
                    </Loading>
                );
            }}
        </Query>
    );
};

CallHistory.propTypes = {
    callHistoryType: string,
    orderId: string,
    firebase: object
};

export default CallHistory;
